import React, { useState } from "react";
import { Cake, Calendar, Users, Send } from "lucide-react";
import { toast } from "sonner";
import Marquee from "../components/Marquee";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const initial = {
  name: "",
  email: "",
  phone: "",
  type: "",
  date: "",
  guests: "",
  message: "",
};

const Devis = () => {
  const [form, setForm] = useState(initial);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.type) {
      toast.error("Merci de renseigner votre nom, e-mail et le type de création.");
      return;
    }
    const saved = JSON.parse(localStorage.getItem("rp_devis") || "[]");
    saved.push({ ...form, createdAt: new Date().toISOString() });
    localStorage.setItem("rp_devis", JSON.stringify(saved));
    toast.success("Votre demande de devis a bien été envoyée ! Je vous réponds sous 48h.");
    setForm(initial);
  };

  return (
    <div>
      <section className="bg-[#FFF6F9] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <span className="font-body text-xs tracking-[0.35em] uppercase text-[#9D184C]">Sur mesure</span>
          <h1 className="font-title text-4xl md:text-6xl text-[#3A2A30] mt-4">Devis sur mesure</h1>
          <p className="font-body text-[#3A2A30]/70 mt-4 max-w-xl mx-auto">
            Racontez-moi votre projet gourmand : je vous réponds avec une proposition personnalisée.
          </p>
        </div>
      </section>

      <Marquee />

      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-5">
            {[
              { icon: Cake, title: "Une création unique", text: "Gâteaux, cupcakes, trompe-l'œil… tout est possible." },
              { icon: Calendar, title: "Anticipez", text: "Pensez à commander au moins 1 semaine à l'avance." },
              { icon: Users, title: "Pour tous vos événements", text: "Mariages, anniversaires, baptêmes, entreprises." },
            ].map((c, i) => (
              <div key={i} className="bg-[#FFF6F9] rounded-2xl p-6 border border-[#F3D9E2] flex gap-4">
                <div className="w-12 h-12 shrink-0 rounded-full bg-[#9D184C] flex items-center justify-center">
                  <c.icon className="text-white" size={22} />
                </div>
                <div>
                  <h3 className="font-title text-lg text-[#3A2A30]">{c.title}</h3>
                  <p className="font-body text-sm text-[#3A2A30]/60 mt-1 leading-relaxed">{c.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-3 bg-white rounded-2xl p-7 md:p-9 border border-[#F3D9E2] shadow-[0_10px_40px_rgba(157,24,76,0.10)]">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="font-body text-sm text-[#3A2A30]">Nom complet *</Label>
                  <Input value={form.name} onChange={set("name")} placeholder="Votre nom" className="bg-[#FFF6F9] border-[#F3D9E2]" />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-sm text-[#3A2A30]">E-mail *</Label>
                  <Input type="email" value={form.email} onChange={set("email")} placeholder="vous@email.fr" className="bg-[#FFF6F9] border-[#F3D9E2]" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="font-body text-sm text-[#3A2A30]">Téléphone</Label>
                  <Input value={form.phone} onChange={set("phone")} placeholder="06 …" className="bg-[#FFF6F9] border-[#F3D9E2]" />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-sm text-[#3A2A30]">Type de création *</Label>
                  <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                    <SelectTrigger className="bg-[#FFF6F9] border-[#F3D9E2]">
                      <SelectValue placeholder="Choisir…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gateau">Gâteau</SelectItem>
                      <SelectItem value="cupcakes">Cupcakes & desserts</SelectItem>
                      <SelectItem value="cookies">Cookies</SelectItem>
                      <SelectItem value="trompe-loeil">Trompe-l'œil</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="font-body text-sm text-[#3A2A30]">Date de l'événement</Label>
                  <Input type="date" value={form.date} onChange={set("date")} className="bg-[#FFF6F9] border-[#F3D9E2]" />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-sm text-[#3A2A30]">Nombre de personnes</Label>
                  <Input type="number" min="1" value={form.guests} onChange={set("guests")} placeholder="Ex : 20" className="bg-[#FFF6F9] border-[#F3D9E2]" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-body text-sm text-[#3A2A30]">Votre projet</Label>
                <Textarea value={form.message} onChange={set("message")} rows={4} placeholder="Décrivez vos envies : thème, saveurs, couleurs…" className="bg-[#FFF6F9] border-[#F3D9E2]" />
              </div>

              <Button type="submit" className="w-full bg-[#9D184C] hover:bg-[#7A0F39] text-white font-body uppercase tracking-wide rounded-full py-6 text-sm">
                <Send size={18} className="mr-2" /> Envoyer ma demande
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Devis;
